/**
 * The channel panel's link to its long-form Help article ("Open full guide in Help", BR-13) and the
 * article's "Guide last checked" date (BR-14). Both read the same article query and render nothing
 * until the article is published (`current`), so a draft or retired guide is never linked.
 */
import { BookOpen } from 'lucide-react';
import { asCalendarDateString, formatCalendarDate } from '@resida/shared';
import { useHelpArticle } from '../../../lib/api.help';
import { useHelp } from '../../help/HelpProvider';
import { Button } from '../../ui/button';

interface HelpGuideProps {
  articleId: string;
  testId: string;
}

function useCurrentArticle(articleId: string) {
  const { data } = useHelpArticle(articleId);
  return data?.status === 'current' ? data : null;
}

export function HelpGuideLink({ articleId, testId }: HelpGuideProps) {
  const article = useCurrentArticle(articleId);
  const { openPanel } = useHelp();
  if (!article) return null;
  return (
    <Button
      type="button"
      variant="link"
      className="h-auto min-h-[44px] shrink-0 px-0 text-primary underline"
      data-testid={testId}
      onClick={() => openPanel({ tab: 'articles', articleId })}
    >
      <BookOpen aria-hidden="true" />
      Open full guide in Help
    </Button>
  );
}

export function GuideLastChecked({ articleId, testId }: HelpGuideProps) {
  const article = useCurrentArticle(articleId);
  if (!article) return null;
  const checked = formatCalendarDate(asCalendarDateString(article.reviewedAt), {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
  return (
    <p className="text-xs text-muted-foreground" data-testid={testId}>
      Guide last checked {checked}. Sites change; if a step looks different, open the guide in Help.
    </p>
  );
}
